import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Car, User, Lock, LogIn } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Toaster } from '../../components/ui/sonner';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      toast.success('Login successful');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-surface p-8 space-y-8">
          {/* Logo */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary mx-auto flex items-center justify-center mb-4">
              <Car className="w-8 h-8 text-[#181B1F]" strokeWidth={1.5} />
            </div>
            <h1 className="font-serif text-3xl text-[#181B1F] font-light">Admin Portal</h1>
            <p className="text-gray-500 text-sm mt-2">Geneva Car Rental Management</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-gray-500">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input-default pl-10"
                  placeholder="Enter username"
                  data-testid="admin-username"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-500">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-default pl-10"
                  placeholder="Enter password"
                  data-testid="admin-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full btn-primary"
              data-testid="admin-login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              <LogIn className="w-4 h-4 ml-2" />
            </Button>
            
            {/* Forgot Password Link */}
            <div className="text-center">
              <Link
                to="/admin/forgot-password"
                className="text-gray-400 hover:text-[#4A7C59] text-sm transition-colors"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </form>

        </div>

        {/* Back to site */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-[#181B1F] text-sm transition-colors"
          >
            ← Back to website
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
